import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { CommonService } from 'src/app/shared/services/common.service';
import { DeviceService } from '../../device.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { ScheduleWindow, MeridianInfo } from './scheduling-window.interface';
import * as moment from 'moment';

@Component({
	selector: 'app-load-scheduling',
	templateUrl: './load-scheduling.component.html',
	styleUrls: ['./load-scheduling.component.css']
})

export class LoadSchedulingComponent implements OnInit {
	@Input() deviceInfo: any = {};

	enableLoadSchedule: boolean = false;
	maxSchedulingRecordsAllowed: number = 10;
	schedulingWindows: ScheduleWindow[] = [];
	changeStage: string = 'queue';

	weekdays: Array<{label: string, id: number}> = [
		{ label: "Sunday", id: 0 },
		{ label: "Monday", id: 1 },
		{ label: "Tuesday", id: 2 },
		{ label: "Wednesday", id: 3 },
		{ label: "Thursday", id: 4 },
		{ label: "Friday", id: 5 },
		{ label: "Saturday", id: 6 }
	];

	constructor(
		private deviceService: DeviceService,
		private translate: TranslateService,
		private notificationMessage: NotificationMessageService,
		private commonService: CommonService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit() {
		return this.formatSchedulingWindows();
	}

	addNewScheduleWindow() {
		if (this.schedulingWindows.length < this.maxSchedulingRecordsAllowed) {
			this.schedulingWindows.push({
				hours: null,
				minutes: null,
				meridian: null,
				durationHours: null,
				durationMinutes: null,
				selectedDays: [],
				invalidFields: {},
			});
		}
	}

	removeScheduleWindow(index: number) {
		this.schedulingWindows.splice(index, 1);
	}

	private formatSchedulingWindows() {
		this.enableLoadSchedule = this.deviceInfo.enable_load_schedule;
		if (this.schedulingWindows.length > 0)
			return;

		this.schedulingWindows = [];

		for (let i = 0; i < this.maxSchedulingRecordsAllowed; i++) {
			const win = this.deviceInfo[`window_${i}`];
			if (win && win.days_mask) {
				const utcTime = moment.utc().hour(win.hours).minute(win.minutes).second(0).millisecond(0);
				const localTime = utcTime.clone().local();
				const dayShift = (localTime.day() - utcTime.day() + this.weekdays.length) % this.weekdays.length;

				// adjust days_mask to user local time
				const selectedDays: number[] = [];
				for (let day = 0; day < this.weekdays.length; day++) {
					if ((win.days_mask >> day) & 1) {
						const localDay = (day + dayShift) % this.weekdays.length;
						selectedDays.push(localDay);
					}
				}

				const { hours, meridian } = this.convertHoursFormat(localTime.hour(), { mode: '24to12' });

				const durationHours = Math.floor(win.duration / 60);
				const durationMinutes = win.duration % 60;

				this.schedulingWindows.push({
					hours,
					minutes: localTime.minute(),
					meridian,
					durationHours,
					durationMinutes,
					selectedDays,
				});
			}
		}
	}

	beforeSubmitFunction(): boolean {
		let isValid = true;

		this.schedulingWindows.forEach((win) => (win.invalidFields = {}));

		this.schedulingWindows.forEach((win) => {
			const invalidFields: any = {};

			if (win.hours === null || win.hours < 1 || win.hours > 12) {
				invalidFields.hours = true;
				isValid = false;
			}

			if (win.minutes === null || win.minutes < 0 || win.minutes > 59) {
				invalidFields.minutes = true;
				isValid = false;
			}

			if (win.durationHours === null || win.durationHours < 0 || win.durationHours > 24) {
				invalidFields.durationHours = true;
				isValid = false;
			}

			if (win.durationMinutes === null || win.durationMinutes < 0 || win.durationMinutes > 59 || (win.durationHours == 24 && win.durationMinutes > 0)) {
				invalidFields.durationMinutes = true;
				isValid = false;
			}

			if (!win.selectedDays || win.selectedDays.length === 0) {
				invalidFields.days = true;
				isValid = false;
			}

			win.invalidFields = invalidFields;
		});

		this.cdr.detectChanges();
		return isValid;
	}

	saveSchedule() {
		if (!this.beforeSubmitFunction()) {
			this.notificationMessage.setMessage(this.translate.instant('g.invalid_input'), { clearOnXTimeNavigate: 1 });
			return;
		}

		const payload: any = {};
		const filledWindows = this.schedulingWindows.filter(win => {
			const hasDays = win.selectedDays && win.selectedDays.length > 0;
			const hasDuration = win.durationHours > 0 || win.durationMinutes > 0;
			return hasDays || hasDuration;
		});

		for (let i = 0; i < this.maxSchedulingRecordsAllowed; i++) {
			const win = filledWindows[i] || {
				hours: 0,
				minutes: 0,
				durationHours: 0,
				durationMinutes: 0,
				selectedDays: [],
				meridian: 'AM',
			};

			const hasDays = win.selectedDays && win.selectedDays.length > 0;
			const hasDuration = win.durationHours > 0 || win.durationMinutes > 0;

			if (hasDays && hasDuration) {
				const converted = this.convertHoursFormat(win.hours, { mode: '12to24', meridian: win.meridian });
				const localTime = moment().hour(converted.hours).minute(win.minutes).second(0).millisecond(0);
				const utcTime = localTime.clone().utc();

				const dayShift = (localTime.day() - utcTime.day() + this.weekdays.length) % this.weekdays.length;

				const totalMinutes = win.durationHours * 60 + win.durationMinutes;

				let daysMask = 0;
				win.selectedDays.forEach((day) => {
					const adjustedDay = (day - dayShift + this.weekdays.length) % this.weekdays.length;
					daysMask |= 1 << adjustedDay;
				});

				payload[`window_${i}`] = {
					hours: utcTime.hour(),
					minutes: utcTime.minute(),
					duration: totalMinutes,
					days_mask: daysMask,
				};
			} else {
				payload[`window_${i}`] = {
					hours: 0,
					minutes: 0,
					duration: 0,
					days_mask: 0,
				};
			}
		}

		payload.enable_load_schedule = this.enableLoadSchedule;

		this.deviceService.saveDeviceSettings(this.deviceInfo.mac_address, payload, this.changeStage, {}, this.deviceService.waitTimeout).subscribe(() => {
			this.notificationMessage.setMessage('globalSuccessMsg', { clearOnXTimeNavigate: 1 });
		});
	}

	validateInput(win: ScheduleWindow, field: string) {
		if (!win.invalidFields) win.invalidFields = {};

		switch (field) {
			case 'startHours':
				win.invalidFields.hours = win.hours === null || win.hours < 1 || win.hours > 12;
				break;
			case 'startMinutes':
				win.invalidFields.minutes = win.minutes === null || win.minutes < 0 || win.minutes > 59;
				break;
			case 'durationHours':
				win.invalidFields.durationHours = win.durationHours === null || win.durationHours < 0 || win.durationHours > 24;
				break;
			case 'durationMinutes':
				win.invalidFields.durationMinutes = win.durationMinutes === null || win.durationMinutes < 0 || win.durationMinutes > 59 || (win.durationHours == 24 && win.durationMinutes > 0);
				break;
			case 'daysMask':
				win.invalidFields.days = !win.selectedDays || win.selectedDays.length === 0;
				break;
		}

		this.cdr.detectChanges();
	}

	canAddNewWindow() {
		return this.schedulingWindows.length < this.maxSchedulingRecordsAllowed;
	}

	convertHoursFormat(hour: number, options: { mode: '12to24' | '24to12', meridian?: 'AM' | 'PM'}) {
		if (options.mode == '24to12') {
			let hours = hour;
			let meridian: MeridianInfo;

			switch (true) {
			case hour === 0:
				hours = 12;
				meridian = MeridianInfo.AM;
				break;
			case hour === 12:
				hours = 12;
				meridian = MeridianInfo.PM;
				break;
			case hour > 12:
				hours = hour - 12;
				meridian = MeridianInfo.PM;
				break;
			default:
				meridian = MeridianInfo.AM;
			}

			return { hours, meridian };
		}

		if (options.mode == '12to24') {
			let hours = hour % 12;

			if (options.meridian == MeridianInfo.PM) 
				hours += 12;

			if (options.meridian == MeridianInfo.AM && hour == 12) 
				hours = 0;

			return { hours, meridian: options.meridian };
		}
	}

	getUTCOffset(zoneId: number): string {
		const zoneName = this.commonService.getZoneName(zoneId);
		const match = zoneName.match(/\(UTC.*?\)/);
		return match ? match[0] : '';
	}
}